import React, { useEffect, useState } from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { Copy } from '@grubhubprod/gh-cookbook-react';
import {
  useSignupStatus,
  SignupStep,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import {
  mapToFormValues,
  getWeekLocDates,
  getFirstInstanceDateFormatted,
  getDayLocFrequency,
} from 'src/models/LineOfCreditModelUtils';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import { ConfirmationSummaryRow } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationSummaryRow/ConfirmationSummaryRow';
import { sdk } from 'src/utilities/sdk';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { capitalize } from 'src/utilities/string-utils';

interface ILocDetails {
  name: string;
  amount: string;
  isRepeating: boolean;
  budgetLength: string;
  dateString: string;
  frequencyString: string;
}

interface ILineOfCreditConfirmationCardProps {
  setBudgetAmountWithFeeAdded: (budgetAmountWithFeeAdded: number) => void;
  onDataLoaded: () => void;
}

export const FEE_PERCENTAGE = 0; // 0% fee -> https://jira.grubhub.com/browse/CELF-46

export function LineOfCreditConfirmationCard({
  setBudgetAmountWithFeeAdded,
  onDataLoaded,
}: ILineOfCreditConfirmationCardProps): JSX.Element {
  const { corpId, getStepNumber } = useSignupStatus();
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();
  const [hasError, setHasError] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [locDetails, setLocDetails] = useState<ILocDetails>();

  async function getLineOfCreditDetails() {
    setHasError(false);
    setIsLoadingData(true);
    try {
      const { results } = await sdk.http.get(
        `/corps/${corpId}/eventmetadata/search?filter_by=ONLY_ACTIVE`
      );

      if (get(results, 'length') > 0) {
        const locDetails = results[0];

        // get and set numerical budget amount for parent component
        const budgetAmount = get(locDetails, 'tag_invites', '0', 'budget');
        if (budgetAmount) {
          const feeAmount = budgetAmount * FEE_PERCENTAGE;
          setBudgetAmountWithFeeAdded(budgetAmount + feeAmount);
        }

        const { frequency, creditName, budgetLength, amount } =
          mapToFormValues(locDetails);

        let dateString = '';
        let frequencyString = '';
        const isRepeating = frequency === 'repeats';

        if (budgetLength === 'week') {
          dateString = getWeekLocDates({
            loc: locDetails,
            format: 'MM/dd/yyyy',
            useDashForDateRange: true,
            useFullDates: true,
          });

          if (isRepeating) {
            frequencyString = 'Repeats every week';
          }
        } else if (budgetLength === 'day') {
          dateString = getFirstInstanceDateFormatted(
            locDetails.first_instance_utc,
            'MM/dd/yyyy'
          );

          if (isRepeating) {
            const { short } = getDayLocFrequency(locDetails);

            frequencyString = `Repeats ${short}`;
          }
        }

        setLocDetails({
          name: creditName,
          amount,
          isRepeating,
          budgetLength,
          dateString,
          frequencyString,
        });
      }
    } catch (error) {
      setHasError(true);
    } finally {
      onDataLoaded();
      setIsLoadingData(false);
    }
  }

  useEffect(() => {
    if (corpId) {
      getLineOfCreditDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (locDetails) {
      onDataLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locDetails]);

  function getDateLabel(): string {
    if (locDetails) {
      const { budgetLength, isRepeating } = locDetails;
      if (budgetLength === 'week') {
        return 'Dates';
      }

      if (budgetLength === 'day' && isRepeating) {
        return 'Starts on';
      }

      if (budgetLength === 'day' && !isRepeating) {
        return 'Date';
      }

      return '';
    }

    return '';
  }

  return (
    <ConfirmationStepCardContainer
      stepName={capitalize(entityName.singular)}
      isLoadingData={isLoadingData}
      hasError={hasError}
      reloadData={getLineOfCreditDetails}
      linkText={locDetails ? 'Edit' : 'Add'}
      stepNumber={getStepNumber(SignupStep.BUDGET)}
      useSecondaryText={!locDetails}
    >
      {locDetails ? (
        <>
          <ConfirmationSummaryRow label="Name">
            {locDetails.name}
          </ConfirmationSummaryRow>
          <ConfirmationSummaryRow label="Amount">
            {`Up to ${locDetails.amount}/person`}
          </ConfirmationSummaryRow>
          <ConfirmationSummaryRow label={getDateLabel()}>
            {locDetails.dateString}
          </ConfirmationSummaryRow>
          {locDetails.isRepeating && (
            <ConfirmationSummaryRow label="Frequency">
              {locDetails.frequencyString}
            </ConfirmationSummaryRow>
          )}
        </>
      ) : (
        <Copy variant="body" color="secondaryText">
          No Line of Credit established. Your team can’t place orders using
          company credit.
        </Copy>
      )}
    </ConfirmationStepCardContainer>
  );
}
